/**
 * 个人设置(设计图 22)与家长端授权 / 注销:/v1/students/current/* 与 /v1/students/guardian/:id/*;
 * 机构导入学员的首次授权引导 /v1/students/:id/consent-setup。
 * 10-02 口径:不满 14 岁授权与注销由监护人办(多家长时撤回 / 确认注销只有主监护人);满 14 岁本人办,主监护人也能撤回。
 */
import type { ApiClient } from './client.js';

export interface ConsentView {
  item: 'platform_use' | 'face_capture' | 'voice_capture';
  label: string;
  state: string;
  granted: boolean;
  at: string | null;
  version: string | null;
  /** 授权人手机(脱敏) */
  by: string | null;
}
export interface DeletionView {
  status: 'pending_guardian' | 'cooling' | 'cancelled' | 'done';
  requestedAt: string;
  coolingEndsAt: string | null;
  /** 冷静期怎么撤销:self = 本人再次登录或在个人设置撤回;guardian = 监护人在家长端撤销 */
  cancelBy?: 'self' | 'guardian';
}
export interface MySettings {
  profile: { id: string; nickname: string; avatar: string; name: string; grade: string | null };
  /** under14 / teen / adult */
  ageBand?: string;
  /** 14 岁以上(含成人):授权与注销由本人办理 */
  selfDecides?: boolean;
  /** 机构导入、还没完成监护人验证 / 本人确认 */
  consentPending?: boolean;
  privacy: { shareCard: boolean };
  board: { participate: boolean; orgName: string | null; platformOpen?: boolean };
  /** 机构规则「成绩分享卡」:false = 机构关了,学生 / 家长的开关不起作用 */
  shareCardAllowed?: boolean;
  consents: ConsentView[];
  account: {
    phone: string;
    phoneIsGuardian: boolean;
    children: { id: string; nickname: string; current: boolean }[];
    guardianBound: boolean;
    org: { name: string; since: string | null } | null;
  };
  /** 规则里的天数(文案跟着规则变):注销冷静期 / 家长邀请码有效期 */
  rules?: { deletionDays: number; inviteDays: number };
  deletion: DeletionView | null;
}
export interface GuardianLink {
  accountId: string;
  name: string;
  phone: string;
  /** mother / father / other;account = 孩子用这个手机号登录(档案登记的监护人) */
  relation: string;
  primary: boolean;
  self: boolean;
  canUnbind: boolean;
  unbindReason: string | null;
}
export interface GuardianSettings {
  student: { id: string; nickname: string; name: string };
  ageBand?: string;
  privacy: { shareCard: boolean };
  consents: ConsentView[];
  /** 机构导入、还没完成监护人验证:家长端给「去完成授权」 */
  consentPending?: boolean;
  /** 孩子 24 小时内请求过、还没授权的项 */
  requested?: ConsentView['item'][];
  me?: { primary: boolean };
  /** 按钮置灰时显示的原因;null = 可以 */
  can?: { grant: string | null; revoke: string | null; deletion: string | null };
  guardians?: GuardianLink[];
  rules?: { deletionDays: number };
  deletion: DeletionView | null;
}
export interface ConsentSetupView {
  student: { id: string; nickname: string; name: string };
  ageBand: string;
  pending: boolean;
  /** 不满 14 岁:验证码发到档案登记的监护人手机(打码);14 岁以上本人确认,不用验证码 */
  needCode: boolean;
  guardianPhone: string | null;
  consents: ConsentView[];
}
/** 家长手机和档案登记的监护人不一致:要档案监护人收到的确认码再提交一次 */
export interface NeedGuardianCode {
  needGuardianCode: true;
  guardianPhone: string;
  resendAfterSec: number;
  devCode?: string;
}
export const GUARDIAN_RELATION_LABELS: Record<string, string> = {
  mother: '母亲',
  father: '父亲',
  other: '其他监护人',
  account: '登录手机',
};

/** 三项授权的说明(设置页与家长端共用);语音采集这一期不在页面上出(字段保留) */
export const CONSENT_UI: Record<ConsentView['item'], { title: string; use: string; off: string }> = {
  platform_use: {
    title: '使用平台',
    use: '个人信息收集与使用:学习、考试、成绩与证书',
    off: '撤回即注销账号(有冷静期)',
  },
  face_capture: {
    title: '人脸采集',
    use: '影像采集:考试检录、抓拍、屏幕共享、第三视角',
    off: '未授权时考试由监考员人工核验',
  },
  voice_capture: {
    title: '语音采集',
    use: '语音实时识别:和数字人语音互动,不留存音频',
    off: '未授权时只能文字互动',
  },
};
/** 页面上显示的授权项(语音采集一期不出) */
export const shownConsents = (list: ConsentView[]) => list.filter((c) => c.item !== 'voice_capture');

/** 平台头像库(皮肤提供插画) */
export const AVATARS = ['a1', 'a2', 'a3', 'a4', 'a5', 'a6'] as const;

export function settingsApi(api: ApiClient) {
  const C = '/v1/students/current';
  const G = (id: string) => `/v1/students/guardian/${id}`;
  const S = (id: string) => `/v1/students/${id}/consent-setup`;
  return {
    mine: () => api.get<MySettings>(`${C}/settings`),
    updateProfile: (patch: { nickname?: string; avatar?: string }) => api.patch<unknown>(C, patch),
    privacy: (shareCard: boolean) => api.patch<{ shareCard: boolean }>(`${C}/privacy`, { shareCard }),
    requestConsent: (item: string) => api.post<{ sent: boolean }>(`${C}/consents/${item}/request`, {}),
    /** 14 岁以上(含成人)本人授权 / 撤回 */
    selfConsent: (item: string, grant: boolean) =>
      api.post<{ studentId: string; consents: ConsentView[] }>(`${C}/consents/${item}`, { grant }),
    /** 学生自己生成家长邀请码(一天最多 5 次) */
    selfInvite: () =>
      api.post<{ studentId: string; code: string; expiresAt: string; days: number }>(
        `${C}/guardian-invite`,
        {},
      ),
    /** 14 岁以上本人注销:给本人手机发确认码 */
    deletionCode: () =>
      api.post<{ resendAfterSec: number; devCode?: string; phoneTail: string }>(`${C}/deletion/code`, {}),
    /** 申请注销:不满 14 岁发给监护人确认;14 岁以上带本人验证码直接进冷静期 */
    requestDeletion: (code?: string) =>
      api.post<DeletionView>(`${C}/deletion`, { acknowledged: true, ...(code ? { code } : {}) }),
    withdrawDeletion: () => api.delete<{ ok: true }>(`${C}/deletion`),
    setup: {
      view: (studentId: string) => api.get<ConsentSetupView>(S(studentId)),
      code: (studentId: string) =>
        api.post<{ resendAfterSec: number; devCode?: string }>(`${S(studentId)}/code`, {}),
      complete: (
        studentId: string,
        body: { code?: string; faceCapture: boolean; voiceCapture: boolean; agreed: boolean },
      ) => api.post<{ ok: boolean; consents: ConsentView[] }>(S(studentId), body),
    },
    guardian: {
      view: (studentId: string) => api.get<GuardianSettings>(`${G(studentId)}/settings`),
      setConsent: (studentId: string, item: string, grant: boolean) =>
        api.post<ConsentView[]>(`${G(studentId)}/consents/${item}`, { grant }),
      privacy: (studentId: string, shareCard: boolean) =>
        api.patch<{ shareCard: boolean }>(`${G(studentId)}/privacy`, { shareCard }),
      confirmDeletion: (studentId: string) => api.post<DeletionView>(`${G(studentId)}/deletion/confirm`, {}),
      cancelDeletion: (studentId: string) => api.post<{ ok: true }>(`${G(studentId)}/deletion/cancel`, {}),
      unbind: (studentId: string, accountId: string) =>
        api.post<{ ok: true }>(`${G(studentId)}/unbind/${accountId}`, {}),
      /** 用邀请码绑定孩子;家长手机和档案监护人不一致时先回 needGuardianCode,带上 guardianCode 再提交 */
      redeem: (body: { code: string; relation: string; guardianCode?: string }) =>
        api.post<{ id: string; name: string } | NeedGuardianCode>('/v1/students/guardian/redeem', body),
    },
  };
}
export type SettingsApi = ReturnType<typeof settingsApi>;
